import { ReactNode, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BunnyVideo } from "@/components/BunnyVideo";
import { NotesGallery } from "@/components/NotesGallery";
import { CourseTheme } from "@/components/CourseTheme";
import { CompanionDynamic } from "@/components/CompanionDynamic";
import { useCompanionLesson } from "@/hooks/useCompanionData";
import { notesFor } from "@/data/notes";

export interface LessonShellProps {
  course: string;
  courseSlug: string;
  lessonOrder: number | string;
  title: string;
  description: string;
  videoUrl: string;
  thumbUrl?: string;
  bookUrl?: string;
  composer?: string;
  genre?: string;
  rhythm?: string;
  accent?: { hex: string; bgClass: string; ringClass: string };
  children: ReactNode;
}

const DEFAULT_ACCENT = { hex: "#f59e0b", bgClass: "bg-amber-500/15", ringClass: "ring-amber-500/40" };

export function LessonShell({
  course,
  courseSlug,
  lessonOrder,
  title,
  description,
  videoUrl,
  thumbUrl,
  bookUrl,
  composer,
  genre,
  rhythm,
  accent = DEFAULT_ACCENT,
  children,
}: LessonShellProps) {
  const notes = notesFor(courseSlug, String(lessonOrder));
  const [searchParams] = useSearchParams();
  /** ?embed=1 — lesson is inside the LMS iframe. Hide site Header and Footer; show full lesson content. */
  const isEmbed = searchParams.get("embed") === "1";
  // Dynamic content from LMS admin panel (Google Sheet → API → here)
  const companionData = useCompanionLesson(courseSlug, lessonOrder);

  // postMessage height sync so the LMS iframe grows to fit this page. Only when
  // actually framed: on a direct visit the "parent" is this window, and Safari
  // logs an error for every message addressed to an origin that isn't there.
  useEffect(() => {
    if (window.parent === window) return;
    const LMS_ORIGIN = "https://learn-music.nathanielschool.com";
    const send = () => {
      // Use offsetHeight (content height) rather than scrollHeight which can include
      // min-height or overflow artifacts that produce extra blank space in the LMS.
      const h = document.body.offsetHeight;
      if (h > 0) window.parent.postMessage({ type: "companion-resize", height: h }, LMS_ORIGIN);
    };
    send();
    const t1 = setTimeout(send, 600);   // after fonts settle
    const t2 = setTimeout(send, 2500);  // after images / video poster settle
    const observer = new ResizeObserver(send);
    observer.observe(document.documentElement);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    // min-h-screen only applies in full-page mode — in embed mode it would report
    // an inflated scrollHeight (at least viewport height) to the LMS, creating
    // blank whitespace at the bottom of the iframe when content is shorter.
    <div className={isEmbed ? "pb-12" : "min-h-screen pb-12"}>
      {/* ── Site header — hidden inside LMS iframe ─────────────────── */}
      {!isEmbed && (
        <Header
          course={`${course} · Lesson ${lessonOrder}`}
          lesson={title}
          courseSlug={courseSlug as any}
          lessonOrder={String(lessonOrder)}
          videoUrl={videoUrl}
        />
      )}

      {/* ── HERO with video — full-width, no side rail ────────────── */}
      <section className="relative border-b border-nsm-border">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-40"
          style={{
            background: `radial-gradient(700px 360px at 30% 20%, ${accent.hex}33, transparent 60%), radial-gradient(700px 360px at 80% 70%, ${accent.hex}22, transparent 60%)`,
          }}
        />
        <div className="mx-auto max-w-5xl px-4 py-10 lg:px-8 lg:py-14">
          <div
            className="text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ color: accent.hex }}
          >
            {course} · Lesson {lessonOrder}
          </div>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight text-nsm-ink lg:text-4xl">
            {title}
          </h1>
          <p className="mt-3 max-w-3xl text-[15px] leading-relaxed text-nsm-dim">{description}</p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {composer && <span className="nsm-chip">✍︎ {composer}</span>}
            {genre && <span className="nsm-chip">♪ {genre}</span>}
            {rhythm && <span className="nsm-chip">⌁ {rhythm}</span>}
          </div>

          {/* A lesson with no video must not hand the hero to an empty 16:9
              placeholder — courses like Sight Reading are notation-first, and
              a dashed empty box above the fold made 30 fully-built lessons
              read as "nothing here" to students and prospects. Keep the
              promise, give it one honest line, and let the lesson lead. */}
          {videoUrl?.trim() ? (
            <div className="mt-6">
              <BunnyVideo src={videoUrl} poster={thumbUrl} title={title} accentColor={accent.hex} lessonOrder={lessonOrder} />
            </div>
          ) : (
            /* The video is not shot yet, but it has a home. A full 16:9 dead
               box read as "this lesson is empty" (a prospect reported exactly
               that); nothing at all loses the promise. So: a real reserved
               slot, sized like a player's control bar, that says what will
               land here without swallowing the fold. */
            <div
              className="mt-6 flex items-center gap-3 rounded-xl border border-dashed px-4 py-3"
              style={{ borderColor: `${accent.hex}55`, background: `${accent.hex}0d` }}
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                style={{ background: `${accent.hex}1f`, color: accent.hex }}
                aria-hidden
              >
                <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor"><path d="M3 2 L12 7 L3 12 Z" /></svg>
              </span>
              <span className="text-[13px] leading-snug text-nsm-dim">
                <span className="font-semibold" style={{ color: accent.hex }}>
                  Jason&rsquo;s video walkthrough lands here.
                </span>{" "}
                It hasn&rsquo;t been filmed yet — the full lesson below is ready
                to play now.
              </span>
            </div>
          )}
        </div>
      </section>

      {/* ── INLINE NOTES — compact strip directly under the video ─── */}
      {notes.length > 0 && (
        <div className="border-y border-nsm-border bg-gradient-to-b from-nsm-bg to-nsm-surface/30">
          <NotesGallery notes={notes} accent={accent.hex} />
        </div>
      )}

      {/* ── BODY (concepts, listen, practice) ───────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <CourseTheme courseSlug={courseSlug}>{children}</CourseTheme>

        {/* Dynamic content from LMS admin — renders only when admin has saved content */}
        {companionData && (
          <CompanionDynamic data={companionData} accent={accent} />
        )}
      </motion.div>

      {/* ── PAGE TOC / book link — shown in full mode only ────────── */}
      {!isEmbed && (
        <section className="mx-auto max-w-5xl px-4 pb-2 pt-10 lg:px-8">
          <div className="rounded-xl border border-nsm-border bg-nsm-card/30 px-4 py-3">
            <div
              className="text-[10px] font-bold uppercase tracking-[0.22em]"
              style={{ color: accent.hex }}
            >
              On this page
            </div>
            <p className="mt-1 text-[13px] leading-snug text-nsm-dim">
              Watch the video, read the handwritten notes, hear every example,
              and practise with the on-screen tools. Nothing is locked behind
              the video — you can study this lesson by reading alone.
            </p>
            {bookUrl && (
              <a
                href={bookUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold hover:underline"
                style={{ color: accent.hex }}
              >
                Open the course book (PDF) →
              </a>
            )}
          </div>
        </section>
      )}

      {/* ── Site footer — hidden inside LMS iframe ─────────────────── */}
      {!isEmbed && <Footer />}
    </div>
  );
}
